import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, IconButton, CircularProgress, Tooltip } from "@material-ui/core";
import { Refresh as RefreshIcon, BrokenImage as BrokenImageIcon, Close as CloseIcon } from "@material-ui/icons";

import { Lightbox } from "react-modal-image";
import api from "../../services/api";
import { isRequestCanceled } from "../../utils/requestCancel";
import { i18n } from "../../translate/i18n";
import { isDarkTheme } from "../../helpers/darkTint";

const useStyles = makeStyles(theme => ({
	imageContainer: {
		display: "inline-block",
		maxWidth: 280,
		maxHeight: 280,
		borderRadius: 8,
		overflow: "hidden",
		lineHeight: 0,
	},
	messageMedia: {
		objectFit: "contain",
		display: "block",
		width: "auto",
		height: "auto",
		maxWidth: 280,
		maxHeight: 280,
		borderRadius: 8,
		cursor: "pointer",
	},
	// O visualizador do react-modal-image só tem um X pequeno no topo; este botão
	// fica fixo embaixo, acima do overlay dele (z-index 5000).
	lightboxCloseButton: {
		position: "fixed",
		bottom: 24,
		left: "50%",
		transform: "translateX(-50%)",
		zIndex: 5001,
		minWidth: 160,
		backgroundColor: "#fff",
		color: "#333",
		fontWeight: 600,
		boxShadow: "0 2px 12px rgba(0,0,0,0.5)",
		"&:hover": {
			backgroundColor: "#e0e0e0",
		},
	},
	stickerMedia: {
		objectFit: "contain",
		width: 168,
		height: 134,
		borderRadius: 0,
		backgroundColor: "transparent",
	},
	errorContainer: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		width: 250,
		height: 200,
		// No escuro o ícone de tentar de novo (primary) é branco: fundo claro o escondia
		backgroundColor: isDarkTheme(theme) ? theme.palette.action.hover : theme.palette.grey[200],
		borderRadius: 8,
		gap: 8,
	},
	errorIcon: {
		fontSize: 48,
		color: theme.palette.grey[500],
	},
	retryButton: {
		color: theme.palette.primary.main,
	},
	loadingContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: 250,
		height: 200,
		backgroundColor: isDarkTheme(theme) ? theme.palette.action.hover : theme.palette.grey[100],
		borderRadius: 8,
	},
	thumbFrame: {
		position: "relative",
		overflow: "hidden",
		width: 250,
		height: 200,
		borderRadius: 8,
		backgroundColor: "#0b141a",
		lineHeight: 0,
	},
	thumbBlurred: {
		width: "100%",
		height: "100%",
		objectFit: "cover",
		filter: "blur(10px)",
		// O scale empurra para fora do overflow:hidden o halo transparente que
		// o blur() produz nas bordas.
		transform: "scale(1.1)",
	},
	thumbScrim: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "rgba(0,0,0,0.28)",
	},
	loadingContainerSticker: {
		width: 168,
		height: 134,
		backgroundColor: "transparent",
		borderRadius: 0,
	},
	errorContainerSticker: {
		width: 168,
		height: 134,
		borderRadius: 0,
		backgroundColor: "transparent",
	},
}));

const ModalImageCors = ({
	imageUrl,
	messageId,
	isSticker = false,
	// Miniatura embutida do WhatsApp, exibida desfocada no lugar da caixa cinza
	// enquanto o blob da imagem real carrega. Sem ela a sequência era
	// "desfocado -> cinza -> nítido": o placeholder da bolha some, este
	// componente monta e mostra o SEU proprio loading. Com ela, uma transicao so.
	placeholderSrc,
	// Com ele o clique abre o visualizador da conversa (anterior/próxima) em vez
	// do lightbox de uma imagem só.
	onOpen,
}) => {
	const classes = useStyles();
	const [fetching, setFetching] = useState(true);
	const [blobUrl, setBlobUrl] = useState("");
	const [hasError, setHasError] = useState(false);
	const [isRetrying, setIsRetrying] = useState(false);
	const [retryAttempted, setRetryAttempted] = useState(false);
	const [lightboxOpen, setLightboxOpen] = useState(false);
	// Lazy-load: o blob só é pedido quando a imagem chega perto da área visível.
	// Um chat com dezenas de imagens disparava todas de uma vez na abertura — e,
	// como a API roda em HTTP/1.1 (6 conexões por host), elas enfileiravam as
	// requisições do próximo ticket aberto.
	const [isVisible, setIsVisible] = useState(
		() => typeof window === "undefined" || typeof window.IntersectionObserver === "undefined"
	);
	const placeholderRef = useRef(null);
	const mountedRef = useRef(true);
	const blobUrlRef = useRef("");
	const manualAbortRef = useRef(null);

	const replaceBlobUrl = useCallback((next) => {
		if (blobUrlRef.current && blobUrlRef.current !== next) {
			window.URL.revokeObjectURL(blobUrlRef.current);
		}
		blobUrlRef.current = next;
		setBlobUrl(next);
	}, []);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			manualAbortRef.current?.abort();
			if (blobUrlRef.current) {
				window.URL.revokeObjectURL(blobUrlRef.current);
				blobUrlRef.current = "";
			}
		};
	}, []);

	useEffect(() => {
		if (isVisible) return undefined;
		const node = placeholderRef.current;
		if (!node) {
			setIsVisible(true);
			return undefined;
		}
		// A lista de mensagens rola dentro de um container próprio: usá-lo como
		// root faz o rootMargin valer ali (pré-carrega um pouco antes de aparecer).
		const root = node.closest("#messagesList") || null;
		const observer = new window.IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					observer.disconnect();
					setIsVisible(true);
				}
			},
			{ root, rootMargin: "400px 0px" }
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [isVisible]);

	// Extrair o messageId da URL se não foi passado como prop
	const extractMessageId = useCallback(() => {
		if (messageId) return messageId;
		// Tenta extrair da URL: geralmente /public/filename onde filename pode ter o ID
		return null;
	}, [messageId]);

	// Função para notificar o backend sobre 404 e solicitar re-download
	const requestRedownload = useCallback(async (msgId, signal) => {
		if (!msgId) {
			console.warn("[ModalImageCors] messageId não disponível para re-download");
			return null;
		}

		try {
			console.log(`[ModalImageCors] 🔄 Solicitando re-download da mídia: ${msgId}`);
			const { data } = await api.post(`/messages/${msgId}/redownload`, null, { signal });
			
			if (data.success && data.newMediaUrl) {
				console.log(`[ModalImageCors] ✅ Re-download bem-sucedido: ${data.newMediaUrl}`);
				return data.newMediaUrl;
			} else {
				console.warn(`[ModalImageCors] ⚠️ Re-download falhou: ${data.message}`);
				return null;
			}
		} catch (error) {
			if (!isRequestCanceled(error)) {
				console.error("[ModalImageCors] ❌ Erro ao solicitar re-download:", error.message);
			}
			return null;
		}
	}, []);

	// Função para carregar a imagem
	const fetchImage = useCallback(async (url, attemptRedownload = true, signal) => {
		const isAborted = () => Boolean(signal?.aborted) || !mountedRef.current;

		if (!url) {
			replaceBlobUrl("");
			setFetching(false);
			setHasError(true);
			return;
		}

		// Always try blob loading first, including cross-origin URLs.
		// When blob fetch fails due CORS/network, we already fallback to direct URL below.
		
		setFetching(true);
		setHasError(false);

		let blobUrlResult = "";
		try {
			const { data, headers } = await api.get(url, {
				responseType: "blob",
				signal,
			});
			const contentType = (headers["content-type"] || "").toLowerCase();
			if (!contentType.startsWith("image/")) {
				throw new Error(`invalid_content_type:${contentType || "unknown"}`);
			}
			blobUrlResult = window.URL.createObjectURL(new Blob([data], { type: headers["content-type"] }));

			// Valida se o blob realmente decodifica como imagem
			await new Promise((resolve, reject) => {
				const testImg = new Image();
				let settled = false;
				const timer = setTimeout(() => {
					if (!settled) {
						settled = true;
						reject(new Error("invalid_image_data:decode_timeout"));
					}
				}, 8000);

				testImg.onload = () => {
					if (!settled) {
						settled = true;
						clearTimeout(timer);
						resolve(true);
					}
				};

				testImg.onerror = () => {
					if (!settled) {
						settled = true;
						clearTimeout(timer);
						reject(new Error("invalid_image_data:decode_error"));
					}
				};

				testImg.src = blobUrlResult;
			});

			if (isAborted()) {
				window.URL.revokeObjectURL(blobUrlResult);
				return;
			}
			replaceBlobUrl(blobUrlResult);
			setFetching(false);
			setHasError(false);
		} catch (error) {
			if (blobUrlResult) window.URL.revokeObjectURL(blobUrlResult);
			// Saiu do chat (ou a URL mudou) antes de terminar: não é falha da mídia,
			// então nada de re-download nem de ícone de erro.
			if (isRequestCanceled(error) || isAborted()) return;

			console.warn("[ModalImageCors] Failed to load image:", url, error.message);

			const isCorsLikeError =
				!error.response &&
				(String(error.message || "").toLowerCase().includes("network error") ||
				 String(error.message || "").toLowerCase().includes("cors"));

			if (isCorsLikeError) {
				console.warn("[ModalImageCors] CORS/network blocked blob fetch. Falling back to direct URL render.");
				replaceBlobUrl("");
				setFetching(false);
				setHasError(false);
				return;
			}
			
			// Detectar erro 404
			const is404 = error.response?.status === 404 || 
			              error.message?.includes("404") ||
			              error.message?.includes("Not Found");
			const isInvalidContentType = error.message?.includes("invalid_content_type:");
			const isInvalidImageData = error.message?.includes("invalid_image_data:");
			
			if ((is404 || isInvalidContentType || isInvalidImageData) && attemptRedownload && !retryAttempted) {
				const msgId = extractMessageId();
				if (msgId) {
					console.log("[ModalImageCors] 📥 Imagem indisponível/inválida, tentando re-download automático...");
					setRetryAttempted(true);
					
					const newUrl = await requestRedownload(msgId, signal);
					if (isAborted()) return;
					if (newUrl) {
						// Tentar carregar a nova URL (sem nova tentativa de re-download)
						await fetchImage(newUrl, false, signal);
						return;
					}
				}
			}
			
			// Se chegou aqui, falhou definitivamente
			replaceBlobUrl("");
			setFetching(false);
			setHasError(true);
		}
	}, [extractMessageId, requestRedownload, retryAttempted, replaceBlobUrl]);

	useEffect(() => {
		if (!isVisible) return undefined;
		const abortController = new AbortController();
		setRetryAttempted(false); // Reset quando URL muda
		fetchImage(imageUrl, true, abortController.signal);
		return () => abortController.abort();
	}, [imageUrl, isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

	// Função para retry manual
	const handleManualRetry = async () => {
		manualAbortRef.current?.abort();
		const abortController = new AbortController();
		manualAbortRef.current = abortController;
		const { signal } = abortController;

		const msgId = extractMessageId();
		if (!msgId) {
			console.warn("[ModalImageCors] messageId não disponível para retry manual");
			// Apenas tentar recarregar a URL original
			fetchImage(imageUrl, false, signal);
			return;
		}

		setIsRetrying(true);
		setRetryAttempted(false);
		
		const newUrl = await requestRedownload(msgId, signal);
		if (signal.aborted || !mountedRef.current) return;
		if (newUrl) {
			await fetchImage(newUrl, false, signal);
		} else {
			setHasError(true);
		}
		
		if (mountedRef.current) setIsRetrying(false);
	};

	// Exibir erro com botão de retry
	if (hasError) {
		return (
			<div className={`${classes.errorContainer} ${isSticker ? classes.errorContainerSticker : ""}`}>
				<BrokenImageIcon className={classes.errorIcon} />
				<Tooltip title="Tentar baixar novamente">
					<IconButton 
						onClick={handleManualRetry} 
						disabled={isRetrying}
						className={classes.retryButton}
						size="small"
					>
						{isRetrying ? <CircularProgress size={20} /> : <RefreshIcon />}
					</IconButton>
				</Tooltip>
			</div>
		);
	}

	// Loading
	if (fetching) {
		if (placeholderSrc) {
			return (
				<div ref={placeholderRef} className={`${classes.thumbFrame} ${isSticker ? classes.loadingContainerSticker : ""}`}>
					<img className={classes.thumbBlurred} src={placeholderSrc} alt="" />
					<div className={classes.thumbScrim}>
						<CircularProgress size={32} style={{ color: "#fff" }} />
					</div>
				</div>
			);
		}

		return (
			<div ref={placeholderRef} className={`${classes.loadingContainer} ${isSticker ? classes.loadingContainerSticker : ""}`}>
				<CircularProgress size={32} />
			</div>
		);
	}

	return (
		isSticker ? (
			<img
				className={classes.stickerMedia}
				src={blobUrl || imageUrl}
				alt="sticker"
			/>
		) : (
			<div className={classes.imageContainer}>
				<img
					className={classes.messageMedia}
					src={blobUrl || imageUrl}
					alt="image"
					onClick={() => (onOpen ? onOpen() : setLightboxOpen(true))}
				/>
				{lightboxOpen && (
					<>
						<Lightbox
							medium={blobUrl || imageUrl}
							large={blobUrl || imageUrl}
							alt="image"
							onClose={() => setLightboxOpen(false)}
						/>
						<Button
							variant="contained"
							startIcon={<CloseIcon />}
							className={classes.lightboxCloseButton}
							onClick={(e) => {
								// O clique não pode subir para a bolha da mensagem (citação, menu).
								e.stopPropagation();
								setLightboxOpen(false);
							}}
						>
							{i18n.t("common.close")}
						</Button>
					</>
				)}
			</div>
		)
	);
};

export default ModalImageCors;
